import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Box,
    Button,
    CardMedia,
    Chip,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageIcon from "@mui/icons-material/Image";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { StayDetail } from "../../services/restserver-openapi";
import EditStayCalender from "./EditStayCalendar/EditStayCalender";
import { asyncChangeStayDuration, asyncUpdateStayStatus } from "../../store/Vanlifer/tourSlice";
import { CompoundEquipment } from "../campsiteDetailComponents/CompoundEquipment";
import { getReadableSiteType, getReadableCompoundType } from "../../utils/typeUtils";
import { EcoFriendlyIcon } from "./EcoFriendlyIcon";
import StayStatusChip from "./StayStatusChip";
import { AgentLabel, getLabelsByCompoundId } from "../../utils/Agent/labels";
import CompoundInlineLabel from "./CompoundInlineLabel";

const STAY_STATUS = {
    PLANNED: 'PLANNED' as StayDetail.stayStatus,
    REQUEST_SENT: 'REQUEST_SENT' as StayDetail.stayStatus,
    CONFIRMED: 'CONFIRMED' as StayDetail.stayStatus,
    DECLINED: 'DECLINED' as StayDetail.stayStatus,
    NO_RESPONSE: 'NO_RESPONSE' as StayDetail.stayStatus,
    CANCELLED: 'CANCELLED' as StayDetail.stayStatus,
    CHANGED: 'CHANGED' as StayDetail.stayStatus,
} as const;

const STATUS_CHANNEL = {
    EMAIL_LINK: 'EMAIL_LINK' as StayDetail.statusChannel,
    PHONE: 'PHONE' as StayDetail.statusChannel,
    DIRECT_PROVIDER: 'DIRECT_PROVIDER' as StayDetail.statusChannel,
    WHATSAPP: 'WHATSAPP' as StayDetail.statusChannel,
    IN_PERSON: 'IN_PERSON' as StayDetail.statusChannel,
    OTHER: 'OTHER' as StayDetail.statusChannel,
} as const;

interface SingleSiteProposalCardProps {
    stay: StayDetail;
    canEdit: boolean;
    canActivateEditMode: boolean;
    showEditAction: boolean;
    editDisabledTooltip: string;
    onToggleEditMode: () => void;
    isDirty: boolean;
    showStayCalendar: boolean;
    expanded: boolean;
    onToggle: () => void;
    alternativesPanel?: React.ReactNode;
    onHoverChange?: (hovered: boolean) => void;
    hovered?: boolean;
}

const SingleSiteProposalCard: React.FC<SingleSiteProposalCardProps> = ({
                                                                            stay,
                                                                              canEdit,
                                                                              canActivateEditMode,
                                                                              showEditAction,
                                                                              editDisabledTooltip,
                                                                             onToggleEditMode,
                                                                             isDirty,
                                                                             showStayCalendar,
                                                                              expanded,
                                                                               onToggle,
                                                                               alternativesPanel,
                                                                               onHoverChange,
                                                                               hovered = false,
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const activeAccentColor = theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main;
    const hoverFrameColor = theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[700];
    const tourId = useAppSelector((state) => state.tour.choosedTourDetail.tourId);
    const tourVanliferId = useAppSelector((state) => state.tour.choosedTourDetail.vanliferId);
    const tourStatus = useAppSelector((state) => state.tour.choosedTourDetail.status);
    const calendarRef = useRef<HTMLDivElement>(null);
    const statusSectionRef = useRef<HTMLDivElement>(null);
    const userSpecificationId = Number(sessionStorage.getItem('userSpecificationID'));
    const canViewStayStatus = tourStatus === 'BOOKED' && !!tourVanliferId && userSpecificationId === tourVanliferId;
    const [selectedStatus, setSelectedStatus] = useState<StayDetail.stayStatus>(
        stay.stayStatus || STAY_STATUS.PLANNED
    );
    const [selectedChannel, setSelectedChannel] = useState<StayDetail.statusChannel | "">(
        stay.statusChannel || ""
    );
    const [statusNote, setStatusNote] = useState(stay.statusNote || "");
    const [dateSaveError, setDateSaveError] = useState<string | null>(null);
    const [openStatusEditor, setOpenStatusEditor] = useState(false);
    const [compoundLabels, setCompoundLabels] = useState<AgentLabel[]>([]);
    const compoundName = stay.stayCompound?.name ?? "Unbenannter Ort";
    const compoundId = stay.stayCompound?.compoundId;
    const compoundType = stay.stayCompound ? getReadableCompoundType(stay.stayCompound) : "—";

    useEffect(() => {
        setSelectedStatus(stay.stayStatus || STAY_STATUS.PLANNED);
        setSelectedChannel(stay.statusChannel || "");
        setStatusNote(stay.statusNote || "");
    }, [stay.stayStatus, stay.statusChannel, stay.statusNote]);

    useEffect(() => {
        let isCancelled = false;

        if (!compoundId) {
            setCompoundLabels([]);
            return () => {
                isCancelled = true;
            };
        }

        getLabelsByCompoundId(compoundId)
            .then((labels) => {
                if (!isCancelled) {
                    setCompoundLabels(labels);
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setCompoundLabels([]);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [compoundId]);

    useEffect(() => {
        if (!openStatusEditor || !expanded) {
            return;
        }

        statusSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpenStatusEditor(false);
    }, [expanded, openStatusEditor]);

    const saveDateChanges = async (from: number, to: number): Promise<void> => {
        if (stay.stayId && tourId) {
            await dispatch(
                asyncChangeStayDuration({
                    tourId,
                    stayId: stay.stayId,
                    newFromDate: from,
                    newToDate: to,
                })
            ).unwrap();
        }
    };

    const handleEditedStayDates = async (from: number, to: number): Promise<void> => {
        try {
            setDateSaveError(null);
            await saveDateChanges(from, to);
        } catch (error) {
            console.error("Saving stay dates failed", error);
            const maybeApiError = error as { body?: { message?: string } };
            setDateSaveError(maybeApiError?.body?.message ?? "Daten konnten nicht gespeichert werden.");
        }
    };

    const city = stay.stayCompound?.city;
    const country = stay.stayCompound?.country;

    const dateRange = useMemo(() => {
        if (!stay.fromDate || !stay.toDate) return "—";
        return `${moment(stay.fromDate).format("DD.MM.YYYY")} – ${moment(stay.toDate).format("DD.MM.YYYY")}`;
    }, [stay.fromDate, stay.toDate]);

    const siteTitle = useMemo(() => {
        if (!stay.staySite) return "—";
        const base = `${getReadableSiteType(stay.staySite)}: ${stay.staySite.label}`;
        return stay.staySite.sizeId != null ? `${base} (Größe ${stay.staySite.sizeId})` : base;
    }, [stay.staySite]);

    const priceLabel = stay.price != null ? `${stay.price.toFixed(2)} CHF` : "Keine Preisangabe";
    const firstImageId = stay.stayCompound?.imageIds?.[0];

    const CompoundImage = () => {
        if (firstImageId) {
            return (
                <CardMedia
                    component="img"
                    src={`${process.env.REACT_APP_API_URL}/image/${firstImageId}`}
                    alt={compoundName}
                    sx={{
                        width: "100%",
                        height: { xs: 200, sm: 240 },
                        objectFit: "cover",
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                />
            );
        }
        return (
            <Box
                sx={{
                    width: "100%",
                    height: { xs: 200, sm: 240 },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "grey.100",
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <ImageIcon fontSize="large" color="disabled" />
            </Box>
        );
    };

    const SectionTitle = ({ children }: { children: React.ReactNode }) => (
        <Typography variant="body2" fontWeight={800} sx={{ mb: 0.5 }}>
            {children}
        </Typography>
    );

    const Muted = ({ children }: { children: React.ReactNode }) => (
        <Typography variant="body2" color="text.secondary">
            {children}
        </Typography>
    );

    const handleEditModeIconClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!canActivateEditMode && !canEdit) {
            return;
        }
        onToggleEditMode();
    };

    const trimmedStatusNote = stay.statusNote?.trim();

    const handleStatusChipClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        if (!(canEdit && canViewStayStatus)) {
            return;
        }

        setOpenStatusEditor(true);
        if (!expanded) {
            onToggle();
            return;
        }

        statusSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpenStatusEditor(false);
    };

    const handleStatusSave = async () => {
        if (!stay.stayId || !tourId) {
            return;
        }

        try {
            await dispatch(asyncUpdateStayStatus({
                tourId,
                stayId: stay.stayId,
                stayStatus: selectedStatus,
                statusChannel: selectedChannel || undefined,
                statusNote: statusNote || undefined,
            })).unwrap();
        } catch (error) {
            console.error("Saving stay status failed", error);
        }
    };

    const visibleCompoundLabels = compoundLabels.slice(0, 2);

    return (
        <Accordion
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={() => onToggle()}
            onMouseEnter={() => onHoverChange?.(true)}
            onMouseLeave={() => onHoverChange?.(false)}
            sx={{
                border: "none",
                boxShadow: canEdit
                    ? `0 0 0 2px ${alpha(activeAccentColor, 0.95)}`
                    : hovered
                        ? `0 0 0 2px ${alpha(hoverFrameColor, 0.95)}`
                        : "none",
                borderRadius: 2,
                overflow: "hidden",
                "&:before": { display: "none" },
                backgroundColor: "transparent",
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
                sx={{
                    px: 2,
                    py: 1,
                    "& .MuiAccordionSummary-content": {
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    },
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            textTransform: "uppercase",
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: 0.6,
                        }}
                        color="text.secondary"
                    >
                        {compoundType}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                        {compoundId ? (
                            <Link
                                component={RouterLink}
                                to={`/vanlifer/Campsite/${compoundId}`}
                                underline="hover"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    fontWeight: 800,
                                    color: "text.primary",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={compoundName}
                            >
                                {compoundName}
                            </Link>
                        ) : (
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={compoundName}
                            >
                                {compoundName}
                            </Typography>
                        )}
                        {visibleCompoundLabels.map((label) => (
                            <CompoundInlineLabel key={label.labelId} label={label} size="compact" />
                        ))}
                        {stay.stayCompound?.sustainable && <EcoFriendlyIcon sx={{ flex: "0 0 auto" }} />}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {dateRange}
                        {city ? ` • ${city}${country ? `, ${country}` : ""}` : ""}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {siteTitle}
                    </Typography>

                    {canEdit && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                                size="small"
                                label="Bearbeiten aktiv"
                                color="secondary"
                            />
                        </Box>
                    )}

                    {canViewStayStatus && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <StayStatusChip
                                    bookableCompoundType={stay.stayCompound?.bookableCompound}
                                    stayStatus={stay.stayStatus}
                                    statusSource={stay.statusSource}
                                    onClick={canEdit ? handleStatusChipClick : undefined}
                                />
                                {isDirty && (
                                    <Chip
                                        size="small"
                                        label="Aktualisierung notwendig!"
                                        sx={{ bgcolor: "error.main", color: "common.white" }}
                                    />
                                )}
                            </Box>
                            {trimmedStatusNote && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.75, fontStyle: "italic" }}
                                >
                                    {trimmedStatusNote}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "0 0 auto" }}>
                    <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <Typography variant="caption" color="text.secondary">
                            Preis
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                            {priceLabel}
                        </Typography>
                    </Box>

                    {showEditAction && (
                        <Tooltip title={!canActivateEditMode && !canEdit ? editDisabledTooltip : canEdit ? "Bearbeiten deaktivieren" : "Bearbeiten aktivieren"}>
                            <span>
                              <IconButton
                                  size="small"
                                  onClick={handleEditModeIconClick}
                                  disabled={!canActivateEditMode && !canEdit}
                                  sx={{
                                      borderRadius: 2,
                                      color: "text.secondary",
                                      "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                                      "&.Mui-disabled": { color: "action.disabled" },
                                  }}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                        </Tooltip>
                    )}

                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2, pt: 1.5 }}>
                <CompoundImage />

                <Divider sx={{ my: 2 }} />

                <SectionTitle>Hinweise</SectionTitle>
                {stay.stayCompound?.guestInformation ? (
                    <Typography variant="body2">{stay.stayCompound.guestInformation}</Typography>
                ) : (
                    <Muted>Keine zusätzlichen Hinweise.</Muted>
                )}

                <Divider sx={{ my: 2 }} />

                <SectionTitle>Adresse</SectionTitle>
                {stay.stayCompound?.addressLine1 ? (
                    <Typography variant="body2">
                        {stay.stayCompound.addressLine1}
                        <br />
                        {stay.stayCompound.zipCode} {stay.stayCompound.city}, {stay.stayCompound.country}
                    </Typography>
                ) : (
                    <Muted>Keine Adressdaten vorhanden.</Muted>
                )}

                <Divider sx={{ my: 2 }} />

                <SectionTitle>Ausstattung</SectionTitle>
                <CompoundEquipment compound={stay.stayCompound} site={stay.staySite} />

                {canEdit && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <SectionTitle>Check-in / Check-out</SectionTitle>

                        <Muted>{dateRange}</Muted>

                        {showStayCalendar && (
                            <Box ref={calendarRef} mt={2}>
                                <EditStayCalender
                                    startDateTimestamp={stay.fromDate}
                                    endDateTimestamp={stay.toDate}
                                    handleEditedStayDates={handleEditedStayDates}
                                    isStartDateFix={true}
                                    isEndDateFix={true}
                                    minDate={Date.now()}
                                    maxDate={Date.now() + 30 * 86400000}
                                />

                                {dateSaveError && (
                                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                                        {dateSaveError}
                                    </Typography>
                                )}
                            </Box>
                        )}

                    </>
                )}

                <Divider sx={{ my: 2 }} />

                <SectionTitle>Stellplatzbeschreibung</SectionTitle>
                {stay.staySite?.offerDescription ? (
                    <Typography variant="body2">{stay.staySite.offerDescription}</Typography>
                ) : (
                    <Muted>Keine Beschreibung vorhanden.</Muted>
                )}

                {canEdit && canViewStayStatus && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box ref={statusSectionRef}>
                        <SectionTitle>Status</SectionTitle>

                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`stay-status-label-${stay.stayId}`}>Status</InputLabel>
                                <Select
                                    labelId={`stay-status-label-${stay.stayId}`}
                                    label="Status"
                                    value={selectedStatus}
                                    onChange={(event: SelectChangeEvent) => setSelectedStatus(event.target.value as StayDetail.stayStatus)}
                                >
                                    <MenuItem value={STAY_STATUS.PLANNED}>Geplant</MenuItem>
                                    <MenuItem value={STAY_STATUS.REQUEST_SENT}>Anfrage gesendet</MenuItem>
                                    <MenuItem value={STAY_STATUS.CONFIRMED}>Bestaetigt</MenuItem>
                                    <MenuItem value={STAY_STATUS.DECLINED}>Abgelehnt</MenuItem>
                                    <MenuItem value={STAY_STATUS.NO_RESPONSE}>Keine Rueckmeldung</MenuItem>
                                    <MenuItem value={STAY_STATUS.CANCELLED}>Gelöscht</MenuItem>
                                    <MenuItem value={STAY_STATUS.CHANGED}>Geaendert</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel id={`stay-channel-label-${stay.stayId}`}>Kanal</InputLabel>
                                <Select
                                    labelId={`stay-channel-label-${stay.stayId}`}
                                    label="Kanal"
                                    value={selectedChannel}
                                    onChange={(event: SelectChangeEvent) => setSelectedChannel(event.target.value as StayDetail.statusChannel | "")}
                                >
                                    <MenuItem value="">Kein Kanal</MenuItem>
                                    <MenuItem value={STATUS_CHANNEL.EMAIL_LINK}>Email-Link</MenuItem>
                                    <MenuItem value={STATUS_CHANNEL.PHONE}>Telefon</MenuItem>
                                    <MenuItem value={STATUS_CHANNEL.DIRECT_PROVIDER}>Direkter Anbieter</MenuItem>
                                    <MenuItem value={STATUS_CHANNEL.WHATSAPP}>WhatsApp</MenuItem>
                                    <MenuItem value={STATUS_CHANNEL.IN_PERSON}>Persoenlich</MenuItem>
                                    <MenuItem value={STATUS_CHANNEL.OTHER}>Sonstiges</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label="Notiz"
                                value={statusNote}
                                onChange={(event) => setStatusNote(event.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleStatusSave}
                                    startIcon={<ChatBubbleOutlineRoundedIcon />}
                                >
                                    Status speichern
                                </Button>
                            </Box>
                        </Box>
                        </Box>
                    </>
                )}

                {canEdit && alternativesPanel && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <SectionTitle>Alternative Gastgeber in der Nähe</SectionTitle>
                        {alternativesPanel}
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default SingleSiteProposalCard;
